import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { UrlTemplateService } from "@/services/urlTemplateService";

export interface PartnerInvitation {
  id: string;
  partner_id: string;
  email: string;
  invitation_token: string;
  status: 'pending' | 'accepted' | 'expired' | 'cancelled';
  invited_by_staff_id?: string;
  expires_at: string;
  created_at: string;
  accepted_at?: string;
}

export const usePartnerInvitations = () => {
  return useQuery({
    queryKey: ["partner-invitations"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('partner_invitations')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error("Error fetching partner invitations:", error);
        throw error;
      }
      return data as PartnerInvitation[];
    }
  });
};

export const useCreatePartnerInvitation = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ partnerId, staffId }: { partnerId: string; staffId: string }) => {
      // Use the unified invitation function
      const { data: invitationId, error } = await supabase.rpc('create_partner_invitation_unified', {
        partner_id_param: partnerId,
        invited_by_staff_id_param: staffId
      });
      
      if (error) {
        throw new Error(error.message || "Failed to create invitation");
      }

      // Get partner details and invitation info from user_invitations
      const { data: invitation, error: inviteError } = await supabase
        .from('user_invitations')
        .select('id, email, organization_id')
        .eq('id', invitationId)
        .single();

      if (inviteError || !invitation) {
        throw new Error("Failed to fetch invitation details");
      }

      // Get partner details
      const { data: partner, error: partnerError } = await supabase
        .from('crm_partners')
        .select('first_name, last_name, email')
        .eq('id', partnerId)
        .single();

      if (partnerError || !partner) {
        throw new Error("Failed to fetch partner details");
      }

      const origin = window.location.origin.replace(/\/$/, '');
      const partnerNameSlug = `${partner.first_name}-${partner.last_name}`.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
      let inviteUrl = `${origin}/onboarding?token=${invitationId}&partner_id=${partnerId}&email=${encodeURIComponent(partner.email)}&partner_name=${encodeURIComponent(partnerNameSlug)}`;
      try {
        const tpl = await UrlTemplateService.getActiveTemplate('partner_invitation');
        if (tpl?.url_template) {
          const variables = {
            baseUrl: (tpl.content_config as any)?.base_url || origin,
            invitationToken: String(invitationId),
            partnerId: String(partnerId),
            partnerName: `${partner.first_name} ${partner.last_name}`,
            partnerNameSlug: partnerNameSlug,
          };
          const processed = UrlTemplateService.processUrlTemplate(tpl.url_template, variables);
          inviteUrl = processed.startsWith('http')
            ? processed
            : `${variables.baseUrl.replace(/\/$/, '')}${processed.startsWith('/') ? '' : '/'}${processed}`;
        }
      } catch (e) {
        console.warn('Falling back to unified onboarding URL:', (e as any)?.message || e);
      }
      
      const { error: emailError } = await supabase.functions.invoke('send-partner-invitation', {
        body: {
          partnerName: `${partner.first_name} ${partner.last_name}`,
          partnerEmail: partner.email,
          inviteUrl: inviteUrl,
          invitationToken: invitationId,
          partnerId: partnerId,
          baseUrl: window.location.origin,
        }
      });

      if (emailError) {
        console.error("Failed to send invitation email:", emailError);
        // Don't throw error here as the invitation was created successfully
      }

      return invitationId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["partner-invitations"] });
      queryClient.invalidateQueries({ queryKey: ["partners"] });
      toast({
        title: "Success",
        description: "Partner invitation sent successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to send invitation: ${error.message}`,
        variant: "destructive",
      });
    }
  });
};

export const useAcceptPartnerInvitation = () => {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ token, userId }: { token: string; userId: string }) => {
      console.log("Accepting invitation:", { token, userId });
      
      // Try standard invitation first
      const { data: invitations, error: inviteError } = await supabase
        .from('user_invitations')
        .select('id, email, status, expires_at')
        .eq('id', token);
      
      let email: string | null = null;
      let status: string | null = null;
      let expires_at: string | null = null;
      let foundInUserInvites = false;
      
      if (inviteError) {
        console.error("Error fetching user_invitations:", inviteError);
      }
      
      if (invitations && invitations.length > 0) {
        email = invitations[0].email;
        status = invitations[0].status;
        expires_at = invitations[0].expires_at;
        foundInUserInvites = true;
      } else {
        // Fallback to partner_invitations by id or token
        const { data: pInv, error: pError } = await supabase
          .from('partner_invitations')
          .select('id, email, status, expires_at')
          .or(`id.eq.${token},invitation_token.eq.${token}`)
          .limit(1);
        
        if (pError) {
          console.error("Error fetching partner_invitations:", pError);
        }
        if (pInv && pInv.length > 0) {
          email = pInv[0].email;
          status = pInv[0].status;
          expires_at = pInv[0].expires_at;
        }
      }
      
      if (!email || !status || !expires_at) {
        throw new Error("Invitation not found");
      }
      
      // Check status and expiry
      if (status !== 'pending') {
        throw new Error("This invitation has already been processed");
      }
      if (new Date(expires_at) <= new Date()) {
        throw new Error("This invitation has expired");
      }

      // Setup partner access based on email
      const { error: setupError } = await supabase.rpc(
        'setup_partner_user_access',
        {
          partner_email: email,
          auth_user_id: userId
        }
      );
      
      if (setupError) {
        console.error("Setup error:", setupError);
        throw new Error(setupError.message || "Failed to setup partner access");
      }

      // Try to mark accepted; ignore RLS errors for partner_invitations
      if (foundInUserInvites) {
        const { error: acceptError } = await supabase
          .from('user_invitations')
          .update({ 
            status: 'accepted',
            accepted_at: new Date().toISOString()
          })
          .eq('id', token);
        if (acceptError) {
          console.warn("Failed to update user_invitations status:", acceptError.message);
        }
      } else {
        const { error: acceptPartnerError } = await supabase
          .from('partner_invitations')
          .update({ 
            status: 'accepted',
            accepted_at: new Date().toISOString()
          })
          .or(`id.eq.${token},invitation_token.eq.${token}`);
        if (acceptPartnerError) {
          console.warn("Failed to update partner_invitations status (likely RLS):", acceptPartnerError.message);
        }
      }

      console.log("Invitation accepted successfully");
      return true;
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Invitation accepted successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to accept invitation: ${error.message}`,
        variant: "destructive",
      });
    }
  });
};